import type {
  DeviceOrientationSample,
  GeoPoint,
  LocationSource,
  PostPhotoMeta,
  PostVisibility,
} from '@citizen-walk/shared-types';
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

/**
 * 投稿の outbox (オフライン送信保留キュー、REQ-4.1.15 MUST)。
 *
 * Phase 5 minimum:
 *   - text-only post を保留 (photo 添付は当面 online 必須)
 *   - IndexedDB に永続化、tab 閉じても保持
 *   - online 復帰時 / 手動リトライで送信
 *
 * docs/01 §3 / docs/03 §7 offline-first 設計に準拠。
 *
 * Phase 5 後半で追加:
 *   - photo を Blob として IndexedDB に保存 → online 復帰時に Storage upload
 *   - Service Worker と連動した自動 flush (現状は navigator online event のみ)
 *   - 楽観 UI: outbox 投稿も AppHome 一覧に即時表示 (pending badge 付き)
 */

const DB_NAME = 'citizen-walk-outbox';
/**
 * DB_VERSION 履歴:
 *  - 1: `posts` store のみ (Phase 5 minimum)
 *  - 2: Sprint BG-DataSettings 19 (U-L4 / U-P7) で `meta` store 追加。
 *       district 単位の lastUpdated 等を保持 (旧 v1 `lib/db.ts` 踏襲)。
 */
const DB_VERSION = 2;
const STORE_NAME = 'posts';
/** Sprint BG-DataSettings 19 (U-L4): district 単位の meta (lastUpdated 等) を入れる generic store。 */
const META_STORE_NAME = 'meta';

export interface OutboxPost {
  /** auto-increment ID (IndexedDB primary key)。 */
  id?: number;
  /**
   * 安定クライアント ID (送信先 Firestore doc の ID に使う)。
   * **冪等送信**のため: 複数 useOutbox インスタンス / 複数 tab / online イベント重複等で同じ item が
   * 並行 flush されても、同一 doc ID へ上書きされるだけで Firestore に重複 doc が作られない。
   * 旧 schema (clientId 未付与) の既存 entry は送信時に `ob-{uid}-{createdLocalAt}` を deterministic
   * fallback として使う (usePosts.sendOutboxItem)。
   */
  clientId?: string;
  /** 作成日時 (ローカル、送信時に serverTimestamp で上書き)。 */
  createdLocalAt: number;
  /** Firestore 送信時の入力データ。 */
  authorUid: string;
  authorNickname: string;
  title: string;
  comment: string | null;
  location: GeoPoint;
  accuracy: number | null;
  source: LocationSource;
  visibility: PostVisibility;
  districtId: string;
  /**
   * 紐づくセッション ID (user 第 2 弾、docs/04 §4-2、未紐付けは null)。
   * optional のため旧 schema (sessionId 未付与) の既存 outbox entry は send 時に null として扱う。
   */
  sessionId?: string | null;
  /**
   * 写真 (preprocess 済 original) を Blob で保存。IndexedDB native Blob 対応。
   * online 復帰時の send で File 復元 → Storage upload。
   * Phase 5 後半 photo Blob outbox 対応 (REQ-4.1.15 完全実装)。
   */
  photoBlob: Blob | null;
  /** thumbnail (400px) を Blob で保存。online 復帰時の send で thumbnail upload に使用。 */
  photoThumbBlob: Blob | null;
  /** photoBlob の MIME type (送信時の File 復元用、通常は 'image/jpeg')。 */
  photoMimeType: string | null;
  /** 写真の幾何メタ (docs/27、360度判定結果)。旧 entry (未付与) は送信時 null 扱い。 */
  photo?: PostPhotoMeta | null;
  /**
   * デバイス方位サンプル (Sprint BF Part 1、GAP-U1)。
   * 投稿時に `useDeviceOrientation().getCurrentOrientation()` で取得した snapshot。
   * 旧 outbox entry (orientation 未付与) は send 時 null として扱う。
   */
  orientation?: DeviceOrientationSample | null;
  /**
   * 投稿フォームの付帯 field 値 (postFields 駆動、PostExtraFieldsForm で入力した値)。
   * オンライン投稿は createPost が直接 sendPostToFirestore に渡すが、オフライン時はここに保持して
   * 送信時 (sendOutboxItem) に復元する。これが無いと「タイトル/位置以外 (category/タイプ 等) が
   * 保存されない」不具合になる。旧 schema (未付与) は null 扱い。
   */
  extraFields?: Record<string, unknown> | null;
  /** 最後の送信試行のエラーメッセージ (UI 表示用、null なら未試行)。 */
  lastError: string | null;
  /** 試行回数 (UI / 自動 retry の最大回数判定用)。 */
  attempts: number;
  /**
   * 送信ステータス (R-28 第 1 弾、Phase 5 後半):
   *   - 'pending' (default): 未送信 + 試行中。online 復帰時 + 手動 flush で自動再試行
   *   - 'failed': MAX_ATTEMPTS 超過で自動マーク。手動 flush では引き続き再試行されるが、
   *     **auto retry on online event は R-28 第 2 弾で抑制済** (skipFailed:true)
   * optional のため既存 entry (旧 schema、status 未付与) は code 側で 'pending' として扱う。
   */
  status?: 'pending' | 'failed';
}

/**
 * 自動 retry の最大試行回数。これ以上失敗したら status='failed' に遷移し、UI で
 * 別 badge 表示する (R-28 第 1 弾)。
 * Phase 5 後半 R-28 後続で expiration (一定期間 / 一定回数で破棄) を追加検討。
 */
export const MAX_ATTEMPTS = 5;

/**
 * IndexedDB schema 型 (reviewer audit 🔴 E 反映)。
 * 旧 `old/apps/user/src/lib/db.ts` の `IDBPDatabase<BousaiDB>` 型付けパターン踏襲。
 * v2 で migration が入る際に store 名 / value 型のミスマッチを compile 時に検出可能に。
 */
/**
 * meta store のエントリ型 (Sprint BG-DataSettings 19、U-L4)。
 *
 * 想定 key 例 (規約):
 *  - `district.{districtId}.lastUpdated` — districts/{id} doc を最後に同期した時刻 (ms epoch)
 *  - `posts.{districtId}.lastFlushAt` — outbox flush の最終成功時刻 (ms epoch)
 *
 * value は JSON serializable な任意値 (IDB native 対応の primitive / object / Blob)。
 */
export interface OutboxMetaEntry {
  key: string;
  value: unknown;
  updatedAt: number;
}

interface OutboxDB extends DBSchema {
  posts: {
    key: number;
    value: OutboxPost;
  };
  meta: {
    key: string;
    value: OutboxMetaEntry;
  };
}

let dbPromise: Promise<IDBPDatabase<OutboxDB>> | null = null;

function getDb(): Promise<IDBPDatabase<OutboxDB>> {
  if (!dbPromise) {
    dbPromise = openDB<OutboxDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        // v1 base: posts store
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        }
        // v2 (Sprint BG-DataSettings 19, U-L4): meta store (key=string、自前 set)。
        // 旧 v1 `lib/db.ts` の meta store 踏襲 (key=string、value=OutboxMetaEntry)。
        // 既存 user の DB は upgrade event で oldVersion<2 → 新 store を追加 (data loss なし)。
        if (oldVersion < 2 && !db.objectStoreNames.contains(META_STORE_NAME)) {
          db.createObjectStore(META_STORE_NAME, { keyPath: 'key' });
        }
      },
    });
  }
  return dbPromise;
}

/**
 * BroadcastChannel: 他 tab で outbox 変化を通知 (reviewer audit ⚠️ C 反映)。
 * storage event は IndexedDB 変化を検知しないため、BroadcastChannel で代替。
 * BroadcastChannel 非対応 (古い browser) では single-tab 動作のみ、cross-tab sync は失われる。
 * 旧 `old/apps/user/src/hooks/useOutboxStatus.ts` の `outbox-changed` カスタムイベントを
 * tab 横断対応に拡張した形。
 */
const BROADCAST_CHANNEL_NAME = 'citizen-walk-outbox-changed';
let bc: BroadcastChannel | null = null;
function getBroadcastChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!bc) bc = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
  return bc;
}

/**
 * 同一タブ内の購読者 (複数 useOutbox インスタンス)。
 * BroadcastChannel は **自分が postMessage したタブには配信しない** 仕様のため、
 * オフライン投稿 (addToOutbox) を行ったタブ自身の useOutbox は BroadcastChannel では更新されない。
 * これを補うため、同一タブの購読者には local listener で直接通知する。
 * (例: 地図を開いたまま未送信投稿が追加/送信された際に pending マーカーを即時反映する)。
 */
const localListeners = new Set<() => void>();

/** 他 tab + 同一タブの購読者に変化を通知 (mutation 後)。useOutbox がリスナーで受信。 */
function notifyOutboxChanged() {
  // 同一タブ (BroadcastChannel 自タブ非配信の補完)。
  for (const cb of [...localListeners]) {
    try {
      cb();
    } catch {
      /* 1 つの listener 失敗が他に波及しないよう握りつぶす */
    }
  }
  // 他 tab。
  getBroadcastChannel()?.postMessage({ type: 'outbox-changed' });
}

/** useOutbox から購読 (同一タブ local + 他 tab BroadcastChannel、unmount 時 unsubscribe)。 */
export function subscribeOutboxChanges(callback: () => void): () => void {
  localListeners.add(callback);
  const channel = getBroadcastChannel();
  const handler = () => callback();
  channel?.addEventListener('message', handler);
  return () => {
    localListeners.delete(callback);
    channel?.removeEventListener('message', handler);
  };
}

/** outbox に追加 (online 復帰時に flush される) */
export async function addToOutbox(post: Omit<OutboxPost, 'id'>): Promise<number> {
  const db = await getDb();
  const id = (await db.add(STORE_NAME, post)) as number;
  notifyOutboxChanged();
  return id;
}

/** outbox 全件取得 (createdLocalAt 昇順)。 */
export async function listOutbox(): Promise<OutboxPost[]> {
  const db = await getDb();
  const all = (await db.getAll(STORE_NAME)) as OutboxPost[];
  return all.sort((a, b) => a.createdLocalAt - b.createdLocalAt);
}

/** outbox の件数。 */
export async function countOutbox(): Promise<number> {
  const db = await getDb();
  return await db.count(STORE_NAME);
}

/**
 * outbox 1 件取得 (primary key 直接 get、O(1) アクセス)。R-28 第 4 弾。
 * `listOutbox().find(i => i.id === id)` の O(N) を IDB の primary key 直接 get に置換し、
 * outbox 件数が増えた場合の効率化。見つからなければ `undefined`。
 */
export async function getOutboxItem(id: number): Promise<OutboxPost | undefined> {
  const db = await getDb();
  return (await db.get(STORE_NAME, id)) as OutboxPost | undefined;
}

/** outbox 1 件削除 (送信成功時)。 */
export async function deleteFromOutbox(id: number): Promise<void> {
  const db = await getDb();
  await db.delete(STORE_NAME, id);
  notifyOutboxChanged();
}

/** outbox 1 件更新 (送信失敗時に lastError / attempts 更新)。 */
export async function updateOutboxItem(item: OutboxPost): Promise<void> {
  const db = await getDb();
  await db.put(STORE_NAME, item);
  notifyOutboxChanged();
}

/** outbox 全クリア (test 用 + 緊急時の手動操作)。 */
export async function clearOutbox(): Promise<void> {
  const db = await getDb();
  await db.clear(STORE_NAME);
  notifyOutboxChanged();
}

/**
 * Sprint BG-DataSettings 19 (U-L4 / U-P7) — meta store helpers。
 *
 * 旧 `old/apps/user/src/lib/db.ts` の `getMeta(key)` / `setMeta(key, value)` を踏襲し、
 * IndexedDB の `meta` store に generic な key-value を永続化する。outbox と DB を共有する
 * 理由は、PWA 第一級 user 操作 (投稿 + 同期 meta) を 1 DB / 1 connection で扱うため (旧踏襲)。
 *
 * 想定用途:
 *  - 地区 doc snapshot の lastUpdated 時刻保持 (offline 起動時 cache 鮮度判定)
 *  - outbox 最終 flush 成功時刻 (UI の last-sync indicator)
 *  - その他 ad-hoc な user 設定 (i18n locale 等は localStorage を別途使用、本 store ではない)
 *
 * BroadcastChannel 通知は posts store のみ (meta は UI に影響しない low-frequency データ前提)。
 */

/**
 * meta entry を取得。未存在は `undefined`。
 * 型不明な value は呼び元で type guard する (旧 v1 踏襲、generic 化は深追いせず)。
 */
export async function getMeta(key: string): Promise<OutboxMetaEntry | undefined> {
  const db = await getDb();
  return (await db.get(META_STORE_NAME, key)) as OutboxMetaEntry | undefined;
}

/**
 * meta entry を書込 (upsert)。`updatedAt` は自動付与 (epoch ms)。
 * 旧 v1 の signature `setMeta(key, value)` 互換 (timestamp は呼び元から指定不可)。
 */
export async function setMeta(key: string, value: unknown): Promise<void> {
  const db = await getDb();
  const entry: OutboxMetaEntry = { key, value, updatedAt: Date.now() };
  await db.put(META_STORE_NAME, entry);
}

/**
 * meta entry を削除。未存在キーでも no-op (例外を投げない、旧 v1 踏襲)。
 */
export async function deleteMeta(key: string): Promise<void> {
  const db = await getDb();
  await db.delete(META_STORE_NAME, key);
}

/**
 * test 用に internal state を reset (mock 切替時)。
 * 本番 bundle には含まれるが index.ts では非公開、外部から呼ばれない設計。
 * Phase 5 後半 `features/offline` の統合 DB に移植する際は同様に test 専用ヘルパを分離。
 */
export function __resetOutboxDb() {
  dbPromise = null;
  localListeners.clear();
  if (bc) {
    bc.close();
    bc = null;
  }
}
